/**
 * Shopping Lists orchestrator — web-layer glue.
 *
 * Pattern per action, per T6 AC #2:
 *
 *   (1) call the pure reducer from @shopista/shared/stores
 *   (2) commit the next state to the Zustand container
 *   (3) persist the relevant slice via `adapters.storage`
 *   (4) talk to the server via `adapters.api`
 *
 * This keeps side effects out of the reducer (CP3) and lets the integration
 * test verify the call order by handing in recording adapters.
 *
 * Error strategy matches legacy: the local commit is optimistic — the UI
 * gets the addition immediately. On server error we roll back with a
 * `removeItem` reducer step and re-throw for the caller to surface. Storage
 * writes are fire-and-forget inside the action (they are idempotent — Dexie
 * `put` overwrites). Callers who need strict consistency can await the
 * returned promise.
 */

import type { QueryClient } from '@tanstack/react-query';
import { keys } from '@shopista/shared/api/hooks';
import {
  addItem,
  removeItem,
  updateItem,
  type Adapters,
} from '@shopista/shared/stores';
import type {
  ActiveShoppingListResponse,
  AddShoppingListItemRequest,
  ShoppingList,
  ShoppingListItem,
  ShoppingListItemWithDetails,
  UpdateShoppingListItemRequest,
} from '@shopista/shared/types';
import type { RootState, RootStore } from './root-store';

export interface ShoppingListsOrchestrator {
  /**
   * Add an item to the active list. Input can be either:
   *   - a fully-hydrated `ShoppingListItemWithDetails` (what legacy passed
   *     after optimistic creation — tests use this shape), OR
   *   - a `{ listId, item: AddShoppingListItemRequest }` request to be sent
   *     to the server.
   *
   * Per T6 AC #2 the order of operations for the WithDetails variant is
   * exactly `reducer -> storage.putItem -> api.ShoppingLists.addItem`.
   */
  handleAddItem(input: {
    listId: string;
    optimistic: ShoppingListItemWithDetails;
    request: AddShoppingListItemRequest;
  }): Promise<ShoppingListItemWithDetails>;

  handleUpdateItem(input: {
    listId: string;
    itemId: string;
    changes: Partial<ShoppingListItemWithDetails>;
    request: UpdateShoppingListItemRequest;
  }): Promise<ShoppingListItem>;

  handleRemoveItem(input: {
    listId: string;
    itemId: string;
  }): Promise<void>;

  handleSetActiveList(input: {
    list: ShoppingList | null;
    items: ShoppingListItemWithDetails[];
  }): void;
}

export function createShoppingListsOrchestrator(
  store: RootStore,
  adapters: Adapters,
  queryClient?: QueryClient
): ShoppingListsOrchestrator {
  function withSlice<T>(fn: (state: RootState) => T): T {
    return fn(store.getState());
  }

  // BUG17: the render surfaces (`useActiveShoppingList`) read the TanStack
  // `active` cache, not the Zustand slice the orchestrator commits to. So the
  // optimistic write must land in the cache to be visible within a frame, and
  // the per-tap forced `/active` refetch (two server requests in series ≈ ~1s
  // on mobile) is dropped for check/quantity. We keep the `all` invalidation
  // (lists index counts — off the row's render path) in every case, and retain
  // the `active` refetch only for a store pick (server-JOINed display fields
  // the optimistic row can't compute).

  /** Invalidate the lists index (`useShoppingLists`); off the row render path. */
  function invalidateAll(): void {
    if (!queryClient) return;
    void queryClient.invalidateQueries({ queryKey: keys.ShoppingLists.all });
  }

  /**
   * Force a refetch of the active-list items view (`useActiveShoppingList`).
   * Used only when server-derived join fields change (store pick) — the
   * optimistic row can't compute `display_store` / `display_*` locally.
   */
  function invalidateActive(): void {
    if (!queryClient) return;
    void queryClient.invalidateQueries({ queryKey: keys.ShoppingLists.active });
  }

  /** Snapshot the current active-list cache so a failed write can restore it. */
  function snapshotActive(): ActiveShoppingListResponse | undefined {
    return queryClient?.getQueryData<ActiveShoppingListResponse>(
      keys.ShoppingLists.active
    );
  }

  /** Restore a previously-snapshotted active-list cache value (rollback). */
  function restoreActive(snapshot: ActiveShoppingListResponse | undefined): void {
    if (!queryClient) return;
    queryClient.setQueryData(keys.ShoppingLists.active, snapshot);
  }

  /** Apply a transform to the active-list cache (no-op if cache is empty). */
  function patchActive(
    fn: (old: ActiveShoppingListResponse) => ActiveShoppingListResponse
  ): void {
    if (!queryClient) return;
    queryClient.setQueryData<ActiveShoppingListResponse | undefined>(
      keys.ShoppingLists.active,
      (old) => (old ? fn(old) : old)
    );
  }

  return {
    async handleAddItem({ listId, optimistic, request }) {
      // BUG17: snapshot the render-layer cache before any optimistic write so a
      // server rejection can restore it (avoids cache/Zustand drift).
      const cacheSnapshot = snapshotActive();
      // (1) pure reducer computes next state
      const current = withSlice((s) => s.ShoppingLists);
      const next = addItem(current, { item: optimistic });
      // (2) commit
      store.setState({ ShoppingLists: next });
      // BUG17: write the optimistic row into the TanStack `active` cache — the
      // surface the UI actually renders — so it appears within a frame, before
      // the server POST. Keep `total` in sync with the cached shape.
      patchActive((old) => ({
        ...old,
        items: [...old.items, optimistic],
        total: old.total + 1,
      }));
      // (3) persist — ShoppingListItem (not WithDetails) is what storage holds
      //     We write the base shape; join fields come from shared/types via the
      //     UI selectors.
      const baseItem = stripDetails(optimistic);
      await adapters.storage.putItem(baseItem);
      // (4) server
      try {
        // BUG6: the API now returns the base `ShoppingListItem` shape (no
        // join fields). The UI active list holds `ShoppingListItemWithDetails`
        // — so we hydrate the join fields (`list_name`, `household_id`,
        // `display_title`, plus the product/offer denorm) from the optimistic
        // row that's already in state. The server response wins for fields
        // it owns (id, timestamps, normalized status, etc.).
        const serverResult = await adapters.api.ShoppingLists.addItem(
          listId,
          request
        );
        // T12.1.8.4 — `addItem` can return a "would create — confirm?" signal for
        // an offer-only add. The optimistic orchestrator only ever adds an
        // EXISTING HouseholdProduct (the Lists/Planning surface), so the server
        // never returns the signal here; treat it as an error if it ever does
        // (the offer-only confirm flow lives in the Deals surface, not here).
        if ('needsConfirmation' in serverResult) {
          throw new Error(
            'addItem returned needsConfirmation for a HouseholdProduct add — unexpected',
          );
        }
        const serverItem = serverResult;
        const reconciledItem = reconcileWithDetails(optimistic, serverItem);
        // Reconcile: if server returned a different id (legacy behavior when
        // optimistic id was a client uuid), swap the optimistic row.
        if (serverItem.id !== optimistic.id) {
          const reconciled = withSlice((s) => s.ShoppingLists);
          const afterRemove = removeItem(reconciled, { itemId: optimistic.id });
          const afterAdd = addItem(afterRemove, { item: reconciledItem });
          store.setState({ ShoppingLists: afterAdd });
          await adapters.storage.deleteItem(optimistic.id);
          await adapters.storage.putItem(stripDetails(reconciledItem));
        } else {
          // Same id — overlay the server-confirmed fields onto the existing
          // detail row. Without this the UI keeps the optimistic timestamps /
          // status the client guessed at.
          const afterServer = withSlice((s) => s.ShoppingLists);
          store.setState({
            ShoppingLists: updateItem(afterServer, {
              itemId: optimistic.id,
              changes: reconciledItem,
            }),
          });
          await adapters.storage.putItem(stripDetails(reconciledItem));
        }
        // BUG17: reconcile the server-confirmed row into the cache (handles the
        // id-swap: drop the optimistic id, keep/replace with the reconciled
        // row). The optimistic row already carries the join fields, so add
        // doesn't need a forced `/active` refetch — `all` keeps the lists index
        // counts fresh.
        patchActive((old) => ({
          ...old,
          items: old.items.map((it) =>
            it.id === optimistic.id ? reconciledItem : it
          ),
        }));
        invalidateAll();
        return reconciledItem;
      } catch (err) {
        // Roll back optimistic commit.
        const after = withSlice((s) => s.ShoppingLists);
        store.setState({
          ShoppingLists: removeItem(after, { itemId: optimistic.id }),
        });
        // BUG17: restore the pre-write cache snapshot (render-layer rollback).
        restoreActive(cacheSnapshot);
        await adapters.storage.deleteItem(optimistic.id).catch(() => {});
        throw err;
      }
    },

    async handleUpdateItem({ listId, itemId, changes, request }) {
      // BUG17: snapshot the render-layer cache before the optimistic write.
      const cacheSnapshot = snapshotActive();
      const before = withSlice((s) => s.ShoppingLists);
      const next = updateItem(before, { itemId, changes });
      store.setState({ ShoppingLists: next });
      // BUG17: flip the row in the TanStack `active` cache now — before the
      // PATCH — so the checkbox / quantity reflects within a frame. `changes`
      // is the same `Partial<ShoppingListItemWithDetails>` the callers pass.
      patchActive((old) => ({
        ...old,
        items: old.items.map((it) =>
          it.id === itemId ? { ...it, ...changes } : it
        ),
      }));
      // A store pick changes server-JOINed display fields the optimistic row
      // can't compute locally (the chosen store's name comes from a JOIN); only
      // then do we force an `/active` refetch. T27.2.7.9.3 — the store anchor is
      // `selected_storelocation_id`; a store-only pick (no offer) changes ONLY
      // that, so detect either field. T27.2.7.9.6 — a pin (`flyerdeal_id`) drives
      // the server-resolved `display_price` / `current_price`, so it needs the
      // same refetch to land the pinned deal's price in the headline + Trip.
      const isStorePick =
        'selected_storelocation_id' in changes ||
        'selected_retaileroffer_id' in changes ||
        'flyerdeal_id' in changes;
      await adapters.storage.updateItem(itemId, stripDetails(changes));
      try {
        const serverItem = await adapters.api.ShoppingLists.updateItem(
          listId,
          itemId,
          request
        );
        // Merge authoritative server fields back into the UI detail row.
        const afterServer = withSlice((s) => s.ShoppingLists);
        store.setState({
          ShoppingLists: updateItem(afterServer, {
            itemId,
            changes: serverItem,
          }),
        });
        // BUG17: reconcile the server-confirmed base fields into the cached row
        // (server wins for fields it owns; join fields preserved — same
        // precedence as `reconcileWithDetails`).
        patchActive((old) => ({
          ...old,
          items: old.items.map((it) =>
            it.id === itemId ? { ...it, ...serverItem } : it
          ),
        }));
        await adapters.storage.putItem(stripDetails(serverItem));
        // BUG17: check + quantity don't change store-derived join fields, so
        // the cache merge is the whole update — no forced `/active` refetch.
        // A store pick DOES change them; retain the refetch in that case so
        // Shopping-mode grouping reflects the new store. `all` always fires
        // (lists-index counts, off the row render path).
        invalidateAll();
        if (isStorePick) invalidateActive();
        return serverItem;
      } catch (err) {
        // Roll back to `before` for this item only.
        const original = before.activeItems.find((it) => it.id === itemId);
        if (original) {
          const afterFail = withSlice((s) => s.ShoppingLists);
          store.setState({
            ShoppingLists: updateItem(afterFail, {
              itemId,
              changes: original,
            }),
          });
          await adapters.storage
            .putItem(stripDetails(original))
            .catch(() => {});
        }
        // BUG17: restore the pre-write cache snapshot (render-layer rollback).
        restoreActive(cacheSnapshot);
        throw err;
      }
    },

    async handleRemoveItem({ listId, itemId }) {
      // BUG17: snapshot the render-layer cache before the optimistic delete.
      const cacheSnapshot = snapshotActive();
      const before = withSlice((s) => s.ShoppingLists);
      const original = before.activeItems.find((it) => it.id === itemId);
      store.setState({ ShoppingLists: removeItem(before, { itemId }) });
      // BUG17: drop the row from the TanStack `active` cache now, so it
      // disappears within a frame. Keep `total` in sync with the cached shape.
      patchActive((old) => ({
        ...old,
        items: old.items.filter((it) => it.id !== itemId),
        total: Math.max(0, old.total - 1),
      }));
      await adapters.storage.deleteItem(itemId);
      try {
        await adapters.api.ShoppingLists.deleteItem(listId, itemId);
        // Removal doesn't change store-derived join fields on other rows, so
        // no forced `/active` refetch — `all` keeps the lists-index counts.
        invalidateAll();
      } catch (err) {
        if (original) {
          const afterFail = withSlice((s) => s.ShoppingLists);
          store.setState({
            ShoppingLists: addItem(afterFail, { item: original }),
          });
          await adapters.storage
            .putItem(stripDetails(original))
            .catch(() => {});
        }
        // BUG17: restore the pre-write cache snapshot (render-layer rollback).
        restoreActive(cacheSnapshot);
        throw err;
      }
    },

    handleSetActiveList({ list, items }) {
      store.setState({
        ShoppingLists: {
          ...store.getState().ShoppingLists,
          activeList: list,
          activeItems: items,
        },
      });
    },
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Merge the server's authoritative base `ShoppingListItem` with the
 * optimistic `ShoppingListItemWithDetails` that already lived in local state.
 *
 * BUG6 context: the POST /items endpoints return the base shape (no join
 * fields). The UI active list, however, expects `WithDetails` rows — that's
 * how the page renders product names, store names, household scoping. The
 * optimistic row already carries those join fields (built from the picked
 * `HouseholdProductWithDetails` and the active list metadata). So the rule is:
 * server fields win for everything the server owns (id, timestamps, status,
 * normalized snapshots), optimistic fields fill the join columns.
 *
 * Order matters: spread `optimistic` first (carries join fields + initial
 * base values), then spread `serverItem` (overrides with server-confirmed
 * base values). Anything `serverItem` doesn't carry (the join columns) is
 * preserved from `optimistic`.
 */
function reconcileWithDetails(
  optimistic: ShoppingListItemWithDetails,
  serverItem: ShoppingListItem
): ShoppingListItemWithDetails {
  return {
    ...optimistic,
    ...serverItem,
  };
}

/**
 * Strip the WithDetails join fields down to the storage row shape. The join
 * fields (display_title, product_name, offer_title, etc.) are hydrated by
 * the UI selectors from the base item + HouseholdProduct + RetailerOffer tables; we
 * never store them directly.
 */
function stripDetails(
  row:
    | ShoppingListItemWithDetails
    | Partial<ShoppingListItemWithDetails>
): ShoppingListItem {
  // T12.0.2.1 — retaileroffers is the durable anchor; FlyerDeals surface at
  // read time via the RetailerOffer's matched FlyerDeal (live-validity predicate).
  // `deal_description` / `deal_end_date` / `is_flyer_item` are read-time-
  // derived join fields and remain discarded here when stripping the local-
  // storage shape. The pre-T12.0.2.1 `selected_flyerdeal_id` storage field
  // is gone from both the type and the DB column.
  const {
    list_name: _listName,
    household_id: _hh,
    product_name: _pn,
    category_name: _cn,
    product_default_quantity: _pdq,
    product_expected_price: _pep,
    product_expected_price_currency: _pepc,
    product_expected_price_notes: _pepn,
    product_notes: _pn2,
    offer_title: _ot,
    offer_brand: _ob,
    current_price: _cp,
    regular_price: _rp,
    price_currency: _pc,
    store_id: _sid,
    store_name: _sn,
    deal_description: _dd,
    deal_end_date: _ded,
    is_flyer_item: _ifi,
    display_title: _dt,
    display_price: _dp,
    display_store: _ds,
    added_by_name: _abn,
    purchased_by_name: _pbn,
    ...base
  } = row as ShoppingListItemWithDetails;
  return base as ShoppingListItem;
}

